/**
 * ports.ts — deterministic spaceport + shuttleport spots per settlement.
 *
 * SWG travel law: SPACEPORTS serve the CITIES (launch to the sector);
 * SHUTTLEPORTS serve cities AND towns (instant surface hops). Spots derive
 * from the siteSeed + terrain alone so CommonsSettlementsModule (placement),
 * AtlasTravelModule (prompts) and the server's proximity checks (which test
 * the settlement CENTER within 260 m) always agree. Spots sit well inside
 * that server range.
 *
 * FLAT-SPOT LAW (high-relief audit 2026-08-05): the old spots were pure
 * seed-polar — terrain-blind — and on rocky-highlands sites they landed on
 * cliff faces (measured ±50 m float/burial across the pad footprint). Every
 * spot now scans its ring for the candidate with the least footprint relief
 * (24 candidates, pure terrainHeight math, mirrored server-side), and each
 * port exports a stamped APRON (terrain_stamp flattens the ground to the
 * apron height) so the structure AND the arrival point stand on level ground.
 * The server does NOT mirror apron heights (it never integrates Y) — only
 * the spot/arrival XZ math must stay byte-identical.
 */
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { terrainHeight } from '@shared/terrain.js';
import { capitalTheta0, capitalMainGate, boulevardAngles, CAPITAL_WALL_R, CAPITAL_PORT_OUT_M, CAPITAL_PORT_SIDE_M, CAPITAL_TERMINAL_OUT_M, CAPITAL_TERMINAL_SIDE_M, campusFrame, CAMPUS_PAD_M, CAMPUS_TERMINAL_M } from '@shared/capitals.js';

function h01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

export interface PortSpot { x: number; z: number; yaw: number }

const SCAN_CANDIDATES = 24;

/** Relief (max−min) of the raw terrain over a disc: center + 8 ring samples. */
function discRelief(seed: string, cx: number, cz: number, r: number): number {
  let lo = terrainHeight(seed, cx, cz);
  let hi = lo;
  for (let i = 0; i < 8; i++) {
    const b = (i / 8) * Math.PI * 2;
    const h = terrainHeight(seed, cx + Math.cos(b) * r, cz + Math.sin(b) * r);
    if (h < lo) lo = h;
    if (h > hi) hi = h;
  }
  return hi - lo;
}

/** Mean raw-terrain height over a disc (the apron's stamped level). */
function discMean(seed: string, cx: number, cz: number, r: number): number {
  let sum = terrainHeight(seed, cx, cz);
  for (let i = 0; i < 8; i++) {
    const b = (i / 8) * Math.PI * 2;
    sum += terrainHeight(seed, cx + Math.cos(b) * r * 0.7, cz + Math.sin(b) * r * 0.7);
  }
  return sum / 9;
}

/**
 * Flattest ring angle: seed-anchored base, 24 evenly-spaced candidates, pick
 * the least footprint relief. `avoidA` biases away from an already-placed
 * port (angular-proximity penalty in metres of fake relief) so the spaceport
 * and shuttle pad never crowd the same arc.
 */
function flattestAngle(
  seed: string, s: SettlementSite, salt: string,
  radius: number, discR: number, avoidA?: number,
): number {
  const base = h01(s.siteSeed, salt) * Math.PI * 2;
  let best = base;
  let bestScore = Infinity;
  for (let k = 0; k < SCAN_CANDIDATES; k++) {
    const a = base + (k / SCAN_CANDIDATES) * Math.PI * 2;
    const cx = s.centerX + Math.cos(a) * radius;
    const cz = s.centerZ + Math.sin(a) * radius;
    let score = discRelief(seed, cx, cz, discR);
    if (avoidA !== undefined) {
      let d = Math.abs(a - avoidA) % (Math.PI * 2);
      if (d > Math.PI) d = Math.PI * 2 - d;
      score += Math.max(0, 1 - d / 1.2) * 6;
    }
    if (score < bestScore - 1e-9) { bestScore = score; best = a; }
  }
  return best;
}

// Pure per-(seed, site, kind) memo — the scan is ~216 terrainHeight evals and
// stampedHeight consults ports per-sample from the chunk worker's hot loop.
const spotMemo = new Map<string, PortSpot | null>();

function memoSpot(seed: string, s: SettlementSite, kind: string, make: () => PortSpot | null): PortSpot | null {
  const key = `${seed}|${s.siteSeed}|${kind}`;
  let v = spotMemo.get(key);
  if (v === undefined) { v = make(); spotMemo.set(key, v); }
  return v;
}


/** Capital wave (phase 3): the ports stand OUTSIDE the MAIN gate (the boulevard
 *  facing the other capital) at wall r + 250 on stamped aprons — the spaceport
 *  left of the boulevard, the shuttle pad right, the terminal kiosk nearer. */
function capitalPortFrame(seed: string, s: SettlementSite): { a: number; r: number } {
  const other = portSites(seed).find((o) => o.tier === 'capital' && o.id !== s.id);
  const th = capitalTheta0(s.siteSeed);
  const main = other ? capitalMainGate(th, other.centerX - s.centerX, other.centerZ - s.centerZ) : 0;
  return { a: boulevardAngles(th)[main], r: CAPITAL_WALL_R + CAPITAL_PORT_OUT_M };
}
/** The Grand Circuit campus (phase 5): its shuttle pad stands CAMPUS_PAD_M from the centre on the
 *  side away from the stadium straight, facing the plaza; no spaceport. */
function campusPad(seed: string, s: SettlementSite, out: number, side: number, yawOff: number): PortSpot | null {
  const caps = portSites(seed).filter((o) => o.tier === 'capital');
  if (caps.length < 2) return null;
  const { t, n } = campusFrame(s.centerX, s.centerZ, caps[0].centerX, caps[0].centerZ, caps[1].centerX, caps[1].centerZ);
  return { x: -n.x * out + t.x * side, z: -n.z * out + t.z * side, yaw: Math.atan2(n.z, n.x) + yawOff };
}

function capitalPort(seed: string, s: SettlementSite, side: number, out: number, yawOff: number): PortSpot {
  const { a, r } = capitalPortFrame(seed, s);
  const rr = CAPITAL_WALL_R + out;
  const tx = Math.cos(a + Math.PI / 2), tz = Math.sin(a + Math.PI / 2);
  void r;
  return { x: Math.cos(a) * rr + tx * side, z: Math.sin(a) * rr + tz * side, yaw: a + yawOff };
}

/** Cities only: the landed-ship terminal on the city's edge ring. */
export function spaceportSpot(seed: string, s: SettlementSite): PortSpot | null {
  if (s.tier === 'capital') return memoSpot(seed, s, 'spaceport', () => capitalPort(seed, s, CAPITAL_PORT_SIDE_M, CAPITAL_PORT_OUT_M, Math.PI / 2));
  if (s.tier !== 'city') return null;
  return memoSpot(seed, s, 'spaceport', () => {
    const a = flattestAngle(seed, s, 'spaceport', 150, 20);
    return { x: Math.cos(a) * 150, z: Math.sin(a) * 150, yaw: a + Math.PI / 2 };
  });
}

/** Cities + towns: the WORK TERMINAL kiosk, just off the center plaza. */
export function terminalSpot(seed: string, s: SettlementSite): PortSpot | null {
  if (s.tier === 'capital') return memoSpot(seed, s, 'terminal', () => capitalPort(seed, s, CAPITAL_TERMINAL_SIDE_M, CAPITAL_TERMINAL_OUT_M, Math.PI));
  if (s.tier === 'circuit') return memoSpot(seed, s, 'terminal', () => campusPad(seed, s, CAMPUS_TERMINAL_M, 30, Math.PI));
  if (s.tier !== 'city' && s.tier !== 'town') return null;
  return memoSpot(seed, s, 'terminal', () => {
    const r = s.tier === 'city' ? 34 : 24;
    const a = flattestAngle(seed, s, 'terminal', r, 5);
    return { x: Math.cos(a) * r, z: Math.sin(a) * r, yaw: a + Math.PI };
  });
}

/** Cities + towns: the shuttle pad, kept off the spaceport's arc. */
export function shuttleSpot(seed: string, s: SettlementSite): PortSpot | null {
  if (s.tier === 'capital') return memoSpot(seed, s, 'shuttle', () => capitalPort(seed, s, -CAPITAL_PORT_SIDE_M, CAPITAL_PORT_OUT_M, 0));
  if (s.tier === 'circuit') return memoSpot(seed, s, 'shuttle', () => campusPad(seed, s, CAMPUS_PAD_M, 0, 0));
  if (s.tier !== 'city' && s.tier !== 'town') return null;
  return memoSpot(seed, s, 'shuttle', () => {
    const r = s.tier === 'city' ? 115 : 62;
    const sport = spaceportSpot(seed, s);
    const avoidA = sport ? Math.atan2(sport.z, sport.x) : undefined;
    const a = flattestAngle(seed, s, 'shuttle', r, 17, avoidA);
    return { x: Math.cos(a) * r, z: Math.sin(a) * r, yaw: a };
  });
}

/** A port's stamped ground apron (world coords): flat to `r`, blended over `falloff`. */
export interface PortApron { x: number; z: number; r: number; falloff: number; h: number }

const apronMemo = new Map<string, PortApron[]>();

/**
 * The stamped aprons for a settlement's ports (client-only consumer:
 * terrain_stamp). Radii cover the structure footprint PLUS the arrival
 * offset (13 m beside the shuttle pad) so arrivals stand on level ground.
 */
export function portAprons(seed: string, s: SettlementSite): PortApron[] {
  const key = `${seed}|${s.siteSeed}`;
  let v = apronMemo.get(key);
  if (v !== undefined) return v;
  v = [];
  const sport = spaceportSpot(seed, s);
  if (sport) {
    const x = s.centerX + sport.x, z = s.centerZ + sport.z;
    v.push({ x, z, r: 18, falloff: 10, h: discMean(seed, x, z, 18) });
  }
  const shut = shuttleSpot(seed, s);
  if (shut) {
    const x = s.centerX + shut.x, z = s.centerZ + shut.z;
    v.push({ x, z, r: 16, falloff: 8, h: discMean(seed, x, z, 16) });
  }
  const term = terminalSpot(seed, s);
  if (term) {
    const x = s.centerX + term.x, z = s.centerZ + term.z;
    v.push({ x, z, r: 3.5, falloff: 4, h: discMean(seed, x, z, 3.5) });
  }
  if (s.tier === 'hamlet') {
    // The hamlet arrival clearing gets a small level apron too — it IS the
    // spawn ground (no shuttle pad exists to carry one).
    const arr = arrivalPoint(seed, s);
    v.push({ x: arr.x, z: arr.z, r: 5, falloff: 5, h: discMean(seed, arr.x, arr.z, 5) });
  }
  apronMemo.set(key, v);
  return v;
}

/** A port STRUCTURE's keep-out disc (world coords) — the hull footprint, not
 *  the walkable apron. Versona rendering projects bodies out of these
 *  (server NPC movement is geometry-blind). */
export interface PortDisc { x: number; z: number; r: number }

const discMemo = new Map<string, PortDisc[]>();

export function portClearances(seed: string, s: SettlementSite): PortDisc[] {
  const key = `${seed}|${s.siteSeed}`;
  let v = discMemo.get(key);
  if (v !== undefined) return v;
  v = [];
  const sport = spaceportSpot(seed, s);
  if (sport) v.push({ x: s.centerX + sport.x, z: s.centerZ + sport.z, r: 15 }); // civa_ship ×1.35
  const shut = shuttleSpot(seed, s);
  if (shut) v.push({ x: s.centerX + shut.x, z: s.centerZ + shut.z, r: 9.6 }); // landing pad ×9
  const term = terminalSpot(seed, s);
  if (term) v.push({ x: s.centerX + term.x, z: s.centerZ + term.z, r: 2 }); // kiosk ×1.6
  discMemo.set(key, v);
  return v;
}

/** WORLD-coords safe arrival point (server mirror — the spawn-stuck law):
 *  every spawn/shuttle/space/recovery landing uses the kept-clear pad, never
 *  the tower-packed settlement core. Cities/towns land on the shuttle pad;
 *  hamlets on a deterministic clearing. */
export function arrivalPoint(seed: string, s: SettlementSite): { x: number; z: number } {
  const pad = shuttleSpot(seed, s);
  if (pad) {
    // BESIDE the pad, not ON it: the landing_pad_structure GLB (fixedScale 9)
    // sits exactly at the shuttle spot, so landing at the spot itself
    // materialized players INSIDE the platform (2026-08-05). The measured
    // safe annulus around the pad: the platform's worst-axis half-extent is
    // ~8.6 m (GLB 1.46×1.91 × scale 9), and the overlap filter keeps every
    // building edge ≥ claimed(8.6) + BUILD_GAP_M(10) = 18.6 m from the pad
    // center — so 13 m along the pad's outward radial is guaranteed clear of
    // BOTH, with the pad + city in front of the arriving player. The 13 m
    // point also sits inside the pad's 16 m stamped apron → level ground.
    const r = Math.hypot(pad.x, pad.z) || 1;
    const off = 13;
    return {
      x: s.centerX + pad.x + (pad.x / r) * off,
      z: s.centerZ + pad.z + (pad.z / r) * off,
    };
  }
  // Hamlet clearing: flat-scanned like the ports (a seed-polar clearing on a
  // rocky-highlands hamlet measured slope 0.84 at the spawn point) + memoized
  // (recovery/safe-spawn paths sweep arrivalPoint over ALL ~312 sites).
  const spot = memoSpot(seed, s, 'arrival', () => {
    const a = flattestAngle(seed, s, 'arrival', 46, 6);
    return { x: Math.cos(a) * 46, z: Math.sin(a) * 46, yaw: a };
  })!;
  return { x: s.centerX + spot.x, z: s.centerZ + spot.z };
}

// Pure per-seed memo (server mirror — reducers/wilds.ts wildsOf pattern).
let siteCache: { seed: string; sites: SettlementSite[] } | null = null;
function portSites(seed: string): SettlementSite[] {
  if (!siteCache || siteCache.seed !== seed) siteCache = { seed, sites: settlementSites(seed) };
  return siteCache.sites;
}

/** Commons spawn: the arrival pad of the city nearest the origin (server
 *  mirror). The client MUST predict this at entry — the boot AOI window
 *  centers on the predicted position, and a window laid around (0,0) leaves
 *  the self row unmirrored (the boot gate then never opens). */
export function commonsSpawnPoint(seed: string): { x: number; z: number } {
  let best: SettlementSite | null = null;
  let bd = Infinity;
  for (const s of portSites(seed)) {
    if (s.tier !== 'city') continue;
    const d = Math.hypot(s.centerX, s.centerZ);
    if (d < bd) { bd = d; best = s; }
  }
  return best ? arrivalPoint(seed, best) : { x: 0, z: 0 };
}
